import * as React from "react";

import {cs}                         from "@Common/react/chain-services";
import {consumeContext}             from "@Common/react/context";
import {DeprecatedModalService}     from "@Common/ui-components/modal-service/modal-service";

import {TwoOptionModal}             from "../../../../common/two-option-modal/two-option-modal";
import {Link}                       from "@Common/react/cst2";
import {Types}                      from "@Common/types";
import {PROGRAM_TYPE}               from "@Xpresso/routes/common/logic/program";

export const ReorderAction = ({next, onReordered}:{
    next        : Link<JSX.Element, Types.Common.UiComponents.ModalService.ModalServiceNext<{item: Types.Apis.Payloads.Next.Item.GetHistory.Item}>>,
    onReordered : (item:Types.Apis.Payloads.Next.Item.GetHistory.Item) => void,
}) => cs(
    consumeContext("session"),
    ["emulateAgentOfOrder", ({session}, next) => session && (
        next(async ({branchId, agentId}) => {
            await session.applyEmulation({
                branchId,
                ... (agentId === "Branch Order") ? {
                    isBranchOrder: true,
                } : {
                    agentId,
                },
            });

            return true;
        })
    )],
    ["errorModal", ({}, next) => errorModal({
        next,
    })],
    ["modal", ({emulateAgentOfOrder, errorModal}, next) => reorderModal({
        emulateAgentOfOrder,
        errorModal,
        next,
        onReordered,
    })],
    ({modal}) => next((modal)),
);


const reorderModal = ({emulateAgentOfOrder, errorModal, next, onReordered}) => cs(
    consumeContext("apis"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    ({apis, userLevels, routerHistory}) => DeprecatedModalService({
        render: (params, close) => ({
            content: <TwoOptionModal {...{
                messageLine1: "This will create a new order using information from the selected order.",
                messageLine2: "Please note that templates are sometimes updated, and may result in a loss of content since the last time ordered. However, you will be able to review and edit this order before placing it.",
                messageLine3: "Do you wish to continue?",
                button1Action: () => close(),
                button2Action: () => reorder({apis, close, emulateAgentOfOrder, errorModal, onReordered, userLevels, item: params.item, routerHistory}),
            }}/>,
        }),
        onPassiveClose: () => close(),
        next,
    }),
);

const errorModal = ({next}) => (
    DeprecatedModalService({
        render: (params, close) => ({
            content: <TwoOptionModal {...{
                messageLine1: "Error!",
                messageLine2: params?.msg ? params.msg : 'The attempt to create a reorder of your order named ' + params.item?.itemName + ' appears to have failed.',
                messageLine3: params?.msg ? '' : "Please try again and contact us if the issue persists.",
                button1Action: () => close(),
            }}/>,
        }),
        onPassiveClose: () => close(),
        next,
    })
);


const reorder = async ({apis, close, emulateAgentOfOrder, errorModal, item, onReordered: emitReordered, userLevels, routerHistory}) => {
    if( userLevels.userLevel === 4 && !item.allowEditOffice ) {
        await errorModal.show({msg: "For this order, you have not been given permission to reorder"});
        close();
    } else if( userLevels.userLevel === 5 && !item.allowEditUser ) {
        await errorModal.show({msg: "For this order, you have not been given permission to reorder"});
        close();
    } else {
        await emulateAgentOfOrder({branchId: item.branch.idBranch, agentId: item.agent.idAgent});
        if( item?.programType === PROGRAM_TYPE.APM2 ) {
            await reorderEvent({apis, eventId: item.metaData.eventId, errorModal, close, item})
        } else {
            await reorderItem({apis, item, errorModal, close, routerHistory})
        }

        emitReordered?.(item);
    }
};

const reorderEvent = async ({apis, eventId, errorModal, close, item}) => {
    const [event, newEvent] = await Promise.all([
        apis.listings.getEvent(eventId),
        apis.next.event.cloneEvent({eventId}),
    ]);

    const newEventId = newEvent["_id"];
    if (!newEventId) {
        await errorModal.show({item});
        close();
        return;
    }

    const appData = await cloneItems({
        appData: event.appData,
        apis,
        eventId: newEventId,
    });
    await apis.listings.setEvent({
        ...newEvent.event,
        appData,
    });
    window.location.replace(`/apm?event_id=${newEventId}`);
};

const cloneItems = async ({apis, appData, eventId}) => {

    const eventTypes = ["newListing", "openHouse", "priceChange", "sold"];
    await Promise.all(Object.keys(appData).map(async (appDataKey) => {
        if (!eventTypes.includes(appDataKey)) {
            delete appData[appDataKey];
        } else {
            delete appData[appDataKey].cartId;

            await Promise.all(Object.keys(appData[appDataKey]).map(async (productKey) => {
                if ((typeof(appData[appDataKey][productKey]) !== 'object' ) || !appData[appDataKey][productKey].itemId) {
                    delete appData[appDataKey][productKey];
                } else {
                    const newItem = await apis.next.item.cloneItem({
                        cloneItemJson: {
                            itemId: appData[appDataKey][productKey].itemId,
                            reOrder: true,
                            eventId,
                            cloneCCState: true,
                        }
                    });
                    if (newItem.itemId) {
                        appData[appDataKey][productKey].itemId = newItem.itemId;
                        appData[appDataKey][productKey].ccStateId = newItem.itemId;
                    } else {
                        delete appData[appDataKey][productKey].itemId;
                        delete appData[appDataKey][productKey].ccStateId;
                    }
                    delete appData[appDataKey][productKey].mailingListId;
                }
            }));
        }
    }));
    return appData;
}


const reorderItem = async ({apis, item, errorModal, close, routerHistory}) => {
    const newItem = await apis.next.item.cloneItem({
        cloneItemJson: {
            itemId: item.itemId,
            reOrder: true,
            ...item.proofEngineType === 0 && {
                cloneCCState: true,
            },
        },
    });
    //console.log(routerHistory)
    if ( newItem.itemId ) {
        routerHistory.push(`/product-options/order/${newItem.itemId}`);
    } else {
        await errorModal.show({msg: newItem?.errors?.msg, item});
        close();
    }
};
